/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { BookOpen, HelpCircle, Keyboard, LifeBuoy, Play, Settings, Upload, Users } from "lucide-react";
import { useState, type ReactElement } from "react";
import { useAuth } from "../../auth/AuthContext";
import { Header } from "../../shared/components/Header";
import { Spotlight } from "../../shared/components/effects/Spotlight";
import { Toaster } from "../../shared/components/toaster";

interface HelpSection {
  readonly id: string;
  readonly title: string;
  readonly icon: React.ElementType;
  readonly content: React.ReactNode;
  readonly roles?: readonly ("VIEWER" | "CONTENT_PROVIDER" | "ADMIN")[];
}

/**
 * Help page with role-based dynamic content
 * Shows different sections based on user role (viewer, provider, admin)
 */
export function Help(): ReactElement {
  const { user } = useAuth();
  const [expandedSection, setExpandedSection] = useState<string | null>("getting-started");

  const isProvider = user?.role === "CONTENT_PROVIDER" || user?.role === "ADMIN";
  const isAdmin = user?.role === "ADMIN";

  const sections: HelpSection[] = [
    {
      id: "getting-started",
      title: "Getting Started",
      icon: BookOpen,
      content: (
        <div className="space-y-4">
          <p className="text-neutral-300">
            Welcome to Lumi! Discover and stream independent films from around the world.
          </p>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Browsing Content</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>
                Use the <strong className="text-white">Browse</strong> page to explore films by country on an
                interactive map
              </li>
              <li>Click on a country to filter content from that region</li>
              <li>Use the filter panel to narrow by genre, year, director, or language</li>
              <li>Click on film markers to see specific titles</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Search</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>
                Press <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs">Cmd+K</kbd> (Mac) or{" "}
                <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs">Ctrl+K</kbd> (Windows) to open search
              </li>
              <li>Search by title, director, genre, or year</li>
              <li>Click a result to view the title details</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Watchlist</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Click the bookmark icon on any title to add it to your watchlist</li>
              <li>Access your watchlist from the Browse page filters</li>
              <li>Continue watching from where you left off on the Home page</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "playback",
      title: "Playback & Streaming",
      icon: Play,
      content: (
        <div className="space-y-4">
          <p className="text-neutral-300">
            Lumi uses adaptive streaming to deliver the best quality based on your connection.
          </p>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Video Quality</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Quality automatically adjusts based on your internet speed</li>
              <li>Click the settings icon in the player to manually select quality</li>
              <li>Available qualities: Auto, 1080p, 720p, 480p, 360p</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Captions & Subtitles</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>
                Press <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs">C</kbd> to toggle captions on/off
              </li>
              <li>Click the CC button in the player to select language</li>
              <li>Adjust caption size and position in settings</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">System Requirements</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Modern browser: Chrome 90+, Firefox 88+, Safari 14+, Edge 90+</li>
              <li>Minimum 5 Mbps internet for HD streaming</li>
              <li>JavaScript enabled</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "keyboard-shortcuts",
      title: "Keyboard Shortcuts",
      icon: Keyboard,
      content: (
        <div className="space-y-4">
          <p className="text-neutral-300">Use these shortcuts to navigate Lumi efficiently.</p>
          <div className="space-y-3">
            <div className="grid grid-cols-[140px_1fr] gap-4 text-sm">
              <div className="space-y-2">
                <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs block text-center">Cmd/Ctrl + K</kbd>
                <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs block text-center">Space</kbd>
                <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs block text-center">← →</kbd>
                <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs block text-center">Shift + ← →</kbd>
                <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs block text-center">F</kbd>
                <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs block text-center">C</kbd>
                <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs block text-center">M</kbd>
                <kbd className="px-2 py-1 bg-neutral-800 rounded text-xs block text-center">↑ ↓</kbd>
              </div>
              <div className="space-y-2 text-neutral-400">
                <div>Open search</div>
                <div>Play / Pause</div>
                <div>Seek ±5 seconds</div>
                <div>Seek ±10 seconds</div>
                <div>Toggle fullscreen</div>
                <div>Toggle captions</div>
                <div>Toggle mute</div>
                <div>Adjust volume</div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "account",
      title: "Account & Profiles",
      icon: Users,
      content: (
        <div className="space-y-4">
          <p className="text-neutral-300">Manage your account settings and profiles from the user menu.</p>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Profiles</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Create separate profiles for different family members</li>
              <li>Each profile has its own watch history and watchlist</li>
              <li>Access profiles from the user menu → Profiles</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Account Settings</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Update email, password, and display name in Account settings</li>
              <li>View your watch history and manage watchlist</li>
              <li>Change app preferences in App Settings</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Privacy</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Your watch history is private to your profile</li>
              <li>We don't share your data with third parties</li>
              <li>You can delete your account at any time</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "content-submission",
      title: "Content Submission",
      icon: Upload,
      roles: ["CONTENT_PROVIDER", "ADMIN"],
      content: (
        <div className="space-y-4">
          <p className="text-neutral-300">
            As a content provider, you can submit films for review and distribution on Lumi.
          </p>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Submission Process</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Access the Provider Portal from the user menu</li>
              <li>Click "Submit New Title" to begin</li>
              <li>Fill in all required metadata (title, year, director, etc.)</li>
              <li>Upload your video file (MP4, MOV, or MKV formats)</li>
              <li>Add subtitles in WebVTT format if available</li>
              <li>Submit for review</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Video Requirements</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Minimum resolution: 720p (1280×720)</li>
              <li>Recommended: 1080p (1920×1080) or higher</li>
              <li>Format: H.264 video, AAC audio</li>
              <li>Max file size: 50GB per title</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Review Process</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Submissions are reviewed within 3-5 business days</li>
              <li>You'll receive email notifications about status changes</li>
              <li>Track submission status in the Provider Portal</li>
              <li>Admins may request changes before approval</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "admin-tools",
      title: "Admin Tools",
      icon: Settings,
      roles: ["ADMIN"],
      content: (
        <div className="space-y-4">
          <p className="text-neutral-300">Administrator tools for managing content, users, and platform settings.</p>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Content Management</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>
                <strong>Review Queue:</strong> Approve or reject provider submissions
              </li>
              <li>
                <strong>Titles Library:</strong> View and edit all titles in the catalog
              </li>
              <li>
                <strong>Bulk Editor:</strong> Edit multiple titles at once
              </li>
              <li>
                <strong>Collections:</strong> Create and manage content collections
              </li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">User Management</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>View and manage all user accounts</li>
              <li>Grant or revoke content provider access</li>
              <li>View user activity and watch history</li>
              <li>Monitor platform usage analytics</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Platform Settings</h4>
            <ul className="text-sm text-neutral-400 space-y-1 list-disc list-inside">
              <li>Configure featured content on the home page</li>
              <li>Manage collections and content rails</li>
              <li>View audit logs for all admin actions</li>
              <li>Monitor system health and performance</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "support",
      title: "Support & Contact",
      icon: LifeBuoy,
      content: (
        <div className="space-y-4">
          <p className="text-neutral-300">Need help? We're here to assist you.</p>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Common Issues</h4>
            <ul className="text-sm text-neutral-400 space-y-2 list-disc list-inside">
              <li>
                <strong className="text-white">Video won't play:</strong> Try refreshing the page, clearing your browser
                cache, or switching to a different browser.
              </li>
              <li>
                <strong className="text-white">Buffering issues:</strong> Check your internet connection speed. Try
                lowering the video quality in player settings.
              </li>
              <li>
                <strong className="text-white">Captions not showing:</strong> Ensure captions are enabled (press C or
                click CC button). Some older titles may not have captions available.
              </li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Contact Support</h4>
            <p className="text-sm text-neutral-400">
              For technical issues or questions not covered here, please contact us at:
            </p>
            <div className="text-sm text-neutral-400 space-y-1">
              <div>
                Email:{" "}
                <a href="mailto:support@lumi.app" className="text-lumi-accent hover:underline">
                  support@lumi.app
                </a>
              </div>
              <div>Response time: Within 24 hours</div>
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-lumi-accent">Platform Version</h4>
            <p className="text-sm text-neutral-400">Lumi v0.1.0 (Alpha)</p>
          </div>
        </div>
      ),
    },
  ];

  // Filter sections based on user role
  const visibleSections = sections.filter((section) => {
    if (!section.roles) return true; // Show to everyone
    if (!user) return false; // Hide role-specific sections from unauthenticated users
    return section.roles.includes(user.role);
  });

  const toggleSection = (id: string) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-black relative">
      <Spotlight />

      {/* Header with nav */}
      <Header />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-8 py-12 pt-24">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <HelpCircle className="w-8 h-8 text-lumi-accent" />
              <h1 className="text-3xl font-bold text-white">Help Center</h1>
            </div>
            <p className="text-neutral-400">
              Everything you need to know about using Lumi
              {isProvider && " as a content provider"}
              {isAdmin && " and managing the platform"}.
            </p>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
            <button
              onClick={() => setExpandedSection("getting-started")}
              className="flex flex-col items-center gap-2 p-4 bg-neutral-900/30 hover:bg-neutral-900/50 border border-neutral-800 rounded-lg transition-colors"
              type="button"
            >
              <BookOpen className="w-5 h-5 text-lumi-accent" />
              <span className="text-xs text-neutral-300">Getting Started</span>
            </button>
            <button
              onClick={() => setExpandedSection("playback")}
              className="flex flex-col items-center gap-2 p-4 bg-neutral-900/30 hover:bg-neutral-900/50 border border-neutral-800 rounded-lg transition-colors"
              type="button"
            >
              <Play className="w-5 h-5 text-lumi-accent" />
              <span className="text-xs text-neutral-300">Playback</span>
            </button>
            <button
              onClick={() => setExpandedSection("keyboard-shortcuts")}
              className="flex flex-col items-center gap-2 p-4 bg-neutral-900/30 hover:bg-neutral-900/50 border border-neutral-800 rounded-lg transition-colors"
              type="button"
            >
              <Keyboard className="w-5 h-5 text-lumi-accent" />
              <span className="text-xs text-neutral-300">Shortcuts</span>
            </button>
            <button
              onClick={() => setExpandedSection("support")}
              className="flex flex-col items-center gap-2 p-4 bg-neutral-900/30 hover:bg-neutral-900/50 border border-neutral-800 rounded-lg transition-colors"
              type="button"
            >
              <LifeBuoy className="w-5 h-5 text-lumi-accent" />
              <span className="text-xs text-neutral-300">Support</span>
            </button>
          </div>

          {/* Sections */}
          <div className="space-y-4">
            {visibleSections.map((section) => {
              const isExpanded = expandedSection === section.id;
              const Icon = section.icon;

              return (
                <div
                  key={section.id}
                  className="bg-neutral-900/30 border border-neutral-800 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-900/50 transition-colors"
                    type="button"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-lumi-accent" />
                      <h2 className="text-lg font-medium text-white">{section.title}</h2>
                    </div>
                    <div className={`transform transition-transform ${isExpanded ? "rotate-180" : ""}`}>
                      <svg
                        className="w-5 h-5 text-neutral-400"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  {isExpanded && <div className="px-6 py-4 border-t border-neutral-800">{section.content}</div>}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-neutral-800 text-center text-sm text-neutral-500">
            <p>Can't find what you're looking for?</p>
            <a href="mailto:support@lumi.app" className="text-lumi-accent hover:underline">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      <Toaster />
    </div>
  );
}
